import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import StockModal from '@/components/StockModal';
import CompareModal from '@/components/CompareModal';
import { fetchStockWithFallback } from '@/lib/stockFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable in-place Stock Modal for non-Dashboard pages (Journal,
// JournalTag, future ones). Solves the prior bug where ticker links
// went to /?stock=TICKER, jumping the user back to the Dashboard.
// Now any page can render <InlineStockModal ticker={...} /> and the
// user stays on /journal (or wherever they were) when they close.
//
// Props:
//   ticker         — string | null. null = closed.
//   onClose        — () => void. Called after the modal closes.
//   onPortfolioChanged — () => void. Optional. Fires only when the
//                    user's portfolio actually changed inside the
//                    modal (buy / sell / DRIP). Pages that depend on
//                    portfolio state can use this to re-fetch their
//                    own data; pages that don't can ignore it.

export default function InlineStockModal({ ticker, onClose, onPortfolioChanged, initialTab }) {
  const { user } = useAuth();
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // Sep 2026 — internal active ticker so in-modal navigation (peer
  // chips, Stock Metrics drill-through, warrant/valuation links, back
  // button) works on pages that render InlineStockModal directly
  // (Triangle standalone, Journal, etc.). Defaults to the `ticker`
  // prop and re-syncs whenever the parent opens a new ticker.
  const [activeTicker, setActiveTicker] = useState(ticker || null);
  const [prefetchedNews, setPrefetchedNews] = useState(null);
  // Round 95bt (Jun 2026) — local Compare modal so the StockModal's
  // "Compare" action-line link doesn't require every caller (Journal,
  // JournalTag, etc.) to wire up its own CompareModal instance. Opens
  // in-place over the inline modal; closing returns to the stock view
  // exactly as it was, no route change.
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTicker, setCompareTicker] = useState(null);
  const [watchlistTickers, setWatchlistTickers] = useState([]);
  const [didChangePortfolio, setDidChangePortfolio] = useState(false);

  // Keep watchlist state in sync so the star toggle inside the modal
  // reflects the current state.
  const fetchWatchlist = useCallback(() => {
    if (!user) { setWatchlistTickers([]); return; }
    axios.get(`${API}/portfolio`, { withCredentials: true })
      .then((r) => setWatchlistTickers((r.data || []).map((p) => p.ticker)))
      .catch(() => {});
  }, [user]);

  useEffect(() => { fetchWatchlist(); }, [fetchWatchlist]);

  // Re-sync the internal active ticker whenever the parent changes the
  // `ticker` prop (opens a different stock, or closes with null).
  useEffect(() => { setActiveTicker(ticker || null); }, [ticker]);

  useEffect(() => {
    let cancelled = false;
    if (!activeTicker) { setOpen(false); setStock(null); return; }
    setOpen(true);
    setLoading(true);
    setPrefetchedNews(null);
    setDidChangePortfolio(false);
    axios.get(`${API}/stocks/${activeTicker}/news`, { withCredentials: true })
      .then((r) => { if (!cancelled) setPrefetchedNews(r.data); })
      .catch(() => {});
    (async () => {
      try {
        const data = await fetchStockWithFallback(activeTicker, {
          timeoutMs: 3500,
          onFresh: (fresh) => { if (!cancelled) setStock(fresh); },
        });
        if (!cancelled) setStock(data);
      } catch {
        if (!cancelled) setStock(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTicker]);

  const handleToggleWatchlist = async () => {
    if (!user || !stock?.ticker) return;
    const t = stock.ticker;
    const inWl = watchlistTickers.includes(t);
    try {
      if (inWl) {
        await axios.delete(`${API}/portfolio/${t}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/portfolio`, { ticker: t }, { withCredentials: true });
      }
      fetchWatchlist();
    } catch {
      // ignore — modal already shows current best-effort state
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Round 71p (Feb 2026): only fire onPortfolioChanged if the user
    // actually changed positions inside the modal (buy/sell/drip).
    // Pages can choose to re-fetch their journal/tag/etc. data on
    // close in case the change matters to them.
    if (didChangePortfolio && onPortfolioChanged) onPortfolioChanged();
    onClose && onClose();
  };

  if (!ticker) return null;

  return (
    <>
      <StockModal
        stock={stock}
        open={open}
        onClose={handleClose}
        loading={loading}
        onSelectStock={(t) => { if (t) setActiveTicker(String(t).toUpperCase()); }}
        isInWatchlist={stock ? watchlistTickers.includes(stock.ticker) : false}
        onToggleWatchlist={handleToggleWatchlist}
        isDark={typeof document !== 'undefined' && document.documentElement.classList.contains('dark')}
        prefetchedNews={prefetchedNews}
        user={user}
        initialTab={initialTab}
        onPositionsChanged={() => setDidChangePortfolio(true)}
        onCompareClick={() => {
          if (stock?.ticker) {
            setCompareTicker(stock.ticker);
            setCompareOpen(true);
          }
        }}
      />
      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        initialTickerA={compareTicker}
        watchlist={[]}
        onTickerClick={() => {}}
      />
    </>
  );
}
